import gsap from 'gsap/gsap-core';

import EventDispatcherModule from '@shcc/modules/EventDispatcherModule';

// Modules
import { scrollModule } from '@shcc/modules/ScrollModule';
import { resizeModule } from '@shcc/modules/ResizeModule';

// Helpers
import { lerp } from '@shcc/modules/StandardEaseModule';
import { sinerp } from '@shcc/modules/StandardEaseModule';

const DEFAULT_DAMPING = 0.06;

class CustomScrollModule extends EventDispatcherModule {
  constructor() {
    super();

    this._tickHandler = this._tickHandler.bind(this);
    this._scrollHandler = this._scrollHandler.bind(this);
    this._resizeHandler = this._resizeHandler.bind(this);

    this.enabled = true;

    const { x, y } = scrollModule.position;
    this._setTarget(x, y, { initial: true });
    this._setPosition(x, y, { initial: true });
    this._setPreviousPosition({ initial: true });
  }

  setup(options = {}) {
    this._damping = this._getDamping(options.damping);
    this._damped = this._getDamped(options.damping);

    this._setupEventListeners();
  }

  destroy() {
    this._removeEventListeners();
  }

  enable() {
    this.enabled = true;
  }

  disable() {
    this.enabled = false;
  }

  _setupEventListeners() {
    if (this._damped) gsap.ticker.add(this._tickHandler);

    resizeModule.addEventListener('resize', this._resizeHandler);
    resizeModule.addEventListener('resize:complete', this._resizeHandler);
    scrollModule.addEventListener('scroll', this._scrollHandler);
  }

  _removeEventListeners() {
    if (this._damped) gsap.ticker.remove(this._tickHandler);

    resizeModule.removeEventListener('resize', this._resizeHandler);
    resizeModule.removeEventListener('resize:complete', this._resizeHandler);
    scrollModule.removeEventListener('scroll', this._scrollHandler);
  }

  _dispatch() {
    if (this.enabled) {
      const payload = this._getEventPayload();
      this.dispatchEvent('update:position', payload);
    }
  }

  _setTarget(x, y, options = {}) {
    if (options.initial) this.target = {};

    this.target.x = x;
    this.target.y = y;
  }

  _setPreviousPosition(options = {}) {
    if (options.initial) this._previousPosition = {};

    this._previousPosition.x = this.position.x;
    this._previousPosition.y = this.position.y;
  }

  _setPosition(x, y, options = {}) {
    if (options.initial) this.position = {};

    this.position.x = Math.round(x * 1000) / 1000;
    this.position.y = Math.round(y * 1000) / 1000;

    if (!options.initial && this._getPositionChanged()) this._dispatch();
  }

  _getPositionChanged() {
    return (
      this.position.x !== this._previousPosition.x ||
      this.position.y !== this._previousPosition.y
    );
  }

  _getDamping(damping) {
    if (!damping) return 0.0;
    if (typeof damping === 'number') return damping;
    return DEFAULT_DAMPING;
  }

  _getDamped(damping) {
    return damping > 0.0;
  }

  _getEventPayload() {
    return {
      context: this,
      ...this.position,
    };
  }

  /**
   *
   * Handlers
   */
  _tickHandler() {
    const x = sinerp(this.position.x, this.target.x, this._damping);
    const y = sinerp(this.position.y, this.target.y, this._damping);

    this._setPosition(x, y);
    this._setPreviousPosition();
  }

  _scrollHandler() {
    const { x, y } = scrollModule.position;

    if (this._damped) {
      this._setTarget(x, y);
      return;
    }

    this._setPosition(x, y);
    this._setPreviousPosition();
  }

  _resizeHandler() {
    const { x, y } = scrollModule.position;

    this._setTarget(x, y);
    this._setPosition(x, y);
    this._setPreviousPosition();
  }
}

export default CustomScrollModule;
export const customScrollModule = new CustomScrollModule({ damping: 5 });
